/**
 * Роутинг WS STOMP `/client/event/office` → FILEATTACHMENTS (WUI-316).
 *
 * Фильтр: `OfficeCallbackHandler.statusChanged`. Парсинг — `parseOfficeCallbackEvent`.
 * Доставка: хук `onOfficeCallbackStatus` элемента (по `object_id` = elementId) или
 * fallback `onWsCallback` без привязки к UI.
 *
 * Инициализация: `initOfficeEventRouter` из `GlobalProvider.onMounted`.
 *
 * @see dev/WUI-316.md §4.7
 */
import type { JsRequest } from '@/services/websocket/types'
import {
  parseOfficeCallbackEvent,
  type OfficeCallbackStatusEvent,
} from '@/services/r7AttachmentEditor.service'
import { getWebSocketService } from '@/services/websocket/websocket.service'
import { logR7OfficeCallbackWsDebug } from '@/common/utils/attachmentDebugLog'
import { patchR7OfficeDocumentContextStorageUri } from '@/common/utils/r7OfficeDocumentContextRegistry'
import { onWsCallback } from '@/common/utils/r7AttachmentEditSession'

/** Регистрация FILEATTACHMENTS для приёма WS office callback. */
export type OfficeCallbackElementRegistration = {
  fieldName?: string
  elementType?: string
  onOfficeCallbackStatus?: (event: OfficeCallbackStatusEvent) => void
}

export type OfficeEventRouterDeps = {
  findElementByObjectId: (objectId: number) => OfficeCallbackElementRegistration | undefined
}

let routerDeps: OfficeEventRouterDeps | null = null
let officeUnsubscribe: (() => void) | null = null

function dispatchToElement(event: OfficeCallbackStatusEvent): void {
  const reg = routerDeps?.findElementByObjectId(event.elementId)
  if (reg?.onOfficeCallbackStatus) {
    reg.onOfficeCallbackStatus(event)
    return
  }

  onWsCallback(event)
}

function routeOfficeEvent(evt: JsRequest): void {
  if ((evt.className ?? evt.class_name) !== 'OfficeCallbackHandler') return
  if ((evt.methodName ?? evt.method_name) !== 'statusChanged') return

  logR7OfficeCallbackWsDebug('ws-inbound', {
    className: evt.className ?? evt.class_name,
    methodName: evt.methodName ?? evt.method_name,
    object_id: evt.objectId ?? evt.object_id,
    args: evt.args,
  })

  const event = parseOfficeCallbackEvent(evt)
  if (!event) {
    logR7OfficeCallbackWsDebug('ws-drop', {
      reason: 'parseOfficeCallbackEvent returned null (нет registry prepare или неверный формат)',
      object_id: evt.objectId ?? evt.object_id,
      args: evt.args,
    })
    return
  }

  logR7OfficeCallbackWsDebug('ws-parsed', { event })
  if (event.documentKey && event.storageUri) {
    patchR7OfficeDocumentContextStorageUri(event.documentKey, event.storageUri)
  }
  dispatchToElement(event)
}

/** Подключить роутер (один раз, из GlobalProvider.onMounted). */
export function initOfficeEventRouter(deps: OfficeEventRouterDeps): () => void {
  routerDeps = deps
  if (!officeUnsubscribe) {
    officeUnsubscribe = getWebSocketService().onOfficeEvent(routeOfficeEvent)
  }
  return () => {
    officeUnsubscribe?.()
    officeUnsubscribe = null
    routerDeps = null
  }
}

/** @internal */
export function __resetOfficeEventRouterForTests(): void {
  officeUnsubscribe?.()
  officeUnsubscribe = null
  routerDeps = null
}
